import "./style.scss";
import { FC, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import LogoSvg from "@/src/svg/default/LogoSvg";
import BtnDefault from "../../../../../BtnDefault/BtnDefault";
import { AOPMaltaCertificateData } from "@/src/types/admissionOffice";
import { Editor } from "@tinymce/tinymce-react";
import { splitHtmlByBlocks, splitHtmlByBlocksWithFontConvert } from "../../../../converterPtToRem";
import AutocompleteInput from "@/src/components/AutocompleteInput/AutocompleteInput";
import { useAutocomplete } from "@/src/hooks/useAutocomplete";
import { admissionOfficeRequests } from "@/src/api/admissionOfficeDocCreateRequests";
import { templatesMaltaEnCertificateSettings } from "@/src/constants/AdmissionOffice/Malta/AOMaltaEnConsts";
import { admissionOfficeWhoseDoc } from "@/src/constants/AdmissionOffice/admissionsOfficeConsts";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import { useDispatch } from "react-redux";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";

interface IAOPFormValues {
  whose_doc: string;
  currentDate: string;
  certificate: string;
  fullName: string;
  body: string;
}

interface AOPConfigProps {
  dataForGeneration: AOPMaltaCertificateData;
  setDataForGeneration: React.Dispatch<React.SetStateAction<AOPMaltaCertificateData>>;
  setBodyForPrev: React.Dispatch<React.SetStateAction<AOPMaltaCertificateData>>;
  setCertificateInfo: React.Dispatch<string>;
}

const today = new Date();
const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AOPConfig: FC<AOPConfigProps> = ({ dataForGeneration, setDataForGeneration, setBodyForPrev, setCertificateInfo }) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<IAOPFormValues>({
    defaultValues: {
      body: "",
      currentDate: formatDateForInput(today),
    },
  });
  const dispatch = useDispatch();
  const [certificateContent, setCertificateContent] = useState<string>("");

  const [isPreviewReady, setIsPreviewReady] = useState(false);
  const [isGenerated, setIsGenerated] = useState(false);

  const certificate = watch("certificate");

  // AUTOCOMPLETE ↓
  const whoseDocAuto = useAutocomplete(admissionOfficeWhoseDoc);
  const certificateAuto = useAutocomplete(Object.keys(templatesMaltaEnCertificateSettings));

  // // EDITOR
  useEffect(() => {
    if (!certificate) return;

    const key = certificate as keyof typeof templatesMaltaEnCertificateSettings;
    const certificateSettings = templatesMaltaEnCertificateSettings[key];

    if (certificateSettings) {
      setCertificateContent(certificateSettings.tinyMCEContent);
    }
  }, [certificate]);

  const generateNewDocument = async () => {
    dispatch(setIsPreloyder(true));
    try {
      let response;
      if (certificate === "Certificate") {
        response = await admissionOfficeRequests.createCertificateDoc(dataForGeneration);
      } else {
        response = await admissionOfficeRequests.createAuthorizationLetterteDoc(dataForGeneration);
      }
      if (response) {
        setIsGenerated(true);
      }
    } catch (error) {
      alert("An unexpected error occurred - write to the developers");
    } finally {
      dispatch(setIsPreloyder(false));
    }
    setIsPreviewReady(false);
  };

  const handlePreview = () => {
    const watchedFields = {
      whose_doc: watch("whose_doc"),
      currentDate: watch("currentDate"),
      fullName: watch("fullName"),
    };
    setDataForGeneration((prev) => ({
      ...prev,
      whose_doc: watchedFields.whose_doc,
      currentDate: watchedFields.currentDate,
      fullName: watchedFields.fullName,
      body: splitHtmlByBlocks(certificateContent),
    }));
    setBodyForPrev((prev) => ({
      ...prev,
      whose_doc: watchedFields.whose_doc,
      currentDate: watchedFields.currentDate,
      fullName: watchedFields.fullName,
      body: splitHtmlByBlocksWithFontConvert(certificateContent),
    }));
    if (certificateContent.trim().length > 0) {
      setIsPreviewReady(true);
      setIsGenerated(false);
      setCertificateInfo(certificate);
    }
  };

  const handleCertificateEditorChange = (newCertificateContent: string) => setCertificateContent(newCertificateContent);

  const handleCopy = async (fieldName: string) => {
    const value = getValues(fieldName as any);
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch (err) {
      console.error("Ошибка копирования", err);
    }
  };

  return (
    <>
      <div className="admission-office-page-config">
        <div className="admission-office-page-config-logo">
          <div className="logo-svg">
            <LogoSvg />
          </div>
          <div className="logo-line"></div>
          <p>Platforma</p>
        </div>
        <p className="admission-office-page-config-title">CERTIFICATE</p>
        <form className="admission-office-page-config-form">
          <div className="form-group-fields">
            <div className="form-group-fields-input whose_doc">
              <AutocompleteInput
                placeholder="Whose"
                value={whoseDocAuto.query}
                onChange={whoseDocAuto.onChange}
                onSelect={(v) => {
                  whoseDocAuto.onSelect(v);
                  setValue("whose_doc", v);
                }}
                isOpen={whoseDocAuto.isOpen}
                options={whoseDocAuto.filtered}
                error={!!errors.body}
                required
                inputProps={{
                  register: register("whose_doc", { required: true }),
                  close: whoseDocAuto.close,
                  setOpen: whoseDocAuto.onClick,
                }}
                identifier={"whose"}
              />
            </div>
            <div className="form-group-fields-input currentDate">
              <div className="form-group-fields-input-label">
                <label htmlFor="currentDate">
                  Date of Issue<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="currentDate" id="currentDate" type="date" {...register("currentDate", { required: true })} />
              {errors.currentDate && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input course">
              <AutocompleteInput
                label="Type of certificate"
                value={certificateAuto.query}
                onChange={certificateAuto.onChange}
                onSelect={(v) => {
                  certificateAuto.onSelect(v);
                  setValue("certificate", v);
                }}
                isOpen={certificateAuto.isOpen}
                options={certificateAuto.filtered}
                error={!!errors.certificate}
                required
                inputProps={{
                  register: register("certificate", { required: true }),
                  close: certificateAuto.close,
                  setOpen: certificateAuto.onClick,
                  setValue,
                  clearErrors,
                  name: "certificate",
                }}
                identifier={"certificate"}
              />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="full-name">
                  Full Name<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="full-mame" id="full-mame" type="text" {...register("fullName", { required: true })} />
              {errors.fullName && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullName")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
          <Editor
            value={certificateContent}
            init={{
              height: 300,
              menubar: false,
              plugins: ["lists", "link", "code", "table"],
              toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
              branding: false,
              toolbar_mode: "wrap",
              licenseKey: "gpl",
              license_key: "gpl" as any,
              content_style: `
                body { font-family: Arial, sans-serif; padding: 1rem; margin: 0; }
                p { margin: 0; }
              `,
            }}
            onEditorChange={handleCertificateEditorChange}
            tinymceScriptSrc="/tinymce/tinymce.min.js"
          />
          <div className="form-group-buttons">
            <div className="form-group-buttons-pack">
              <BtnDefault onClick={handlePreview} reverse={true}>
                <p>Preview doc</p>
                <AODSvgExport.PreviewSvg />
              </BtnDefault>
              <BtnDefault onClick={handleSubmit(generateNewDocument)} disabled={!isPreviewReady}>
                <p>Generate doc</p>
                <AODSvgExport.GenerateSvg />
              </BtnDefault>
              <BtnDefault disabled={!isGenerated}>
                <p>Download PDF</p>
                <AODSvgExport.DownloadSvg />
              </BtnDefault>
            </div>
            <div className="form-group-buttons-refresh" onClick={() => window.location.reload()}>
              <BtnDefault onlyIcon={true}>
                <AODSvgExport.RefreshSvg />
              </BtnDefault>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default AOPConfig;
